import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { Globals } from 'app/_globals';
import { ContractorService } from 'app/_services/contractor.service';
import { NotificationService } from 'app/_services/notification.service';
import { RolePermissionService, SkylarkPermission } from 'app/_services/role-permission.service';
import { SiteWorkersService } from 'app/_services/site-workers.service';
import { environment } from 'environments/environment';
import { SortEvent } from 'primeng/api';

@Component({
  selector: 'contractor-workers',
  templateUrl: './contractor-workers.component.html',
})
export class ContractorWorkersComponent implements OnInit {
  public missingSiteOwnerLink = false;

  public contractorWorkers: any[] = [];
  private selectedUser: any;
  public tabitems: any[] = [];

  public selectedContractor: any;
  public selectedContractorId: any;

  public sidebarVisible = false;
  public isReadOnly: boolean;

  // pagination
  public pageSizes: any = environment.pageSizes;
  public pagination: any;
  @ViewChild('paginator', { static: true }) paginator;

  // Sorting and Filtering
  public defaultSortField = 'user.lastName';
  public defaultSortOrder = 1;
  private sortOrderToText = (sortOrder: number) => { return sortOrder === 1 ? 'asc' : 'desc'; }
  public selectedSort = [this.defaultSortField + ',' + this.sortOrderToText(this.defaultSortOrder)];

  public togglableColumns = [
    { displayName: 'Email', default: true },
    { displayName: 'Username', default: false },
    { displayName: 'Phone', default: false },
  ];
  public columnOptionsSidebarVisible: boolean;
  public tableOptions = {
    visibleColumns: [],
  }

  public routeRole: any;

  private siteWorkers: any;

  public siteOwnerSkylarkUserId: any;

  constructor(
    public loader: LoadingBarService,
    public activatedRoute: ActivatedRoute,
    public router: Router,
    public globals: Globals,
    public fb: FormBuilder,
    public notificationService: NotificationService,
    public contractorService: ContractorService,
    public siteWorkersService: SiteWorkersService,
    private rolePermissionService: RolePermissionService,
  ) {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.selectedContractorId = params.contractorId;
    });

    this.routeRole = globals.user.routeRole;

    this.tabitems = [
      { path: '/' + this.routeRole + '/contractors/', title: 'All Contractors', class: 'back-link' },
      { path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/details', title: 'Details' },
      ...(this.routeRole === 'authorising-officer' ? [] : [
        { path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/certificates', title: 'Certificates' },
      ]),
      { path: '/' + this.routeRole + '/contractors/' + this.selectedContractorId + '/workers', title: 'Workers' },
    ];

    this.isReadOnly = !this.rolePermissionService.hasPermission(SkylarkPermission.WRITE__CONTRACTORS);

    this.siteOwnerSkylarkUserId = this.globals.siteOwnerUsers[0].skylarkUserId;
  }

  ngOnInit() {
    this.setupColumnOptions();
    this.resetPagination();
    this.getSelectedContractorCompany();
  }

  public getSelectedContractorCompany() {
    this.contractorService.getContractorById(this.selectedContractorId).subscribe((data: any) => {
      this.selectedContractor = data;

      this.getAllContractorWorkers();

      this.missingSiteOwnerLink = !this.selectedContractor.maintenanceSiteOwners.some(mso => mso.siteOwnerUser.skylarkUserId === this.siteOwnerSkylarkUserId);

    });
  }

  public getAllContractorWorkers() {
    this.contractorService.getAllWorkersForContractor(this.selectedContractorId, this.pagination, this.selectedSort).subscribe((data: any) => {
      this.contractorWorkers = data.body;
      this.pagination.totalRecords = data.totalRecords;

      if (this.routeRole === 'power-station-owner' || this.routeRole === 'power-station-manager') {
        this.getAllSiteWorkersAndCompare();
      }
    });
  }

  public getAllSiteWorkersAndCompare() {
    const siteId = this.globals.powerStationOwner.mainSiteId;
    const filters = [
      { key: 'company', value: this.selectedContractor.name },
    ];

    const pagination = { size: 1_000_000, page: 0 };

    this.siteWorkersService.getAllWorkerForSiteWithFilters(siteId, pagination, undefined, filters).subscribe((data: any) => {
      this.siteWorkers = data.body;

      this.contractorWorkers.forEach(contractorWorker => {
        contractorWorker.isApprovedOnSite = this.siteWorkers.some(siteWorker => contractorWorker.id === siteWorker.workerUser.id);
      });
    });
  }

  public resetPagination() {
    this.pagination = {
      size: this.pageSizes[0],
      page: 0,
      totalRecords: 0,
    };
    this.goToFirstPage();
  }

  paginate(event) {
    this.pagination.size = event.rows;
    this.pagination.page = event.page;

    this.getAllContractorWorkers();
  }

  public async onRowSelect(event) {
    this.selectedUser = event.data;
    this.router.navigate(
      ['/', this.routeRole, 'workers', this.selectedUser.id, 'manage-competencies'],
      { queryParams: { backLink: `/${this.routeRole}/contractors/${this.selectedContractorId}/workers` } }
    );
  }

  public customSort(sort: SortEvent) {
    const sortDirection = this.sortOrderToText(sort.order);

    this.selectedSort = [sort.field + ',' + sortDirection];

    this.goToFirstPage();

    this.getAllContractorWorkers();
  }

  private goToFirstPage() {
    this.pagination.page = 0;      // Backend: reset page index to fetch from API
    if (this.paginator) {
      this.paginator.first = 0;    // Frontend: update visual page indicator (PrimeNg)
    }
  }

  public resetColumnOptions() {
    const defaultColumns = this.togglableColumns;

    this.tableOptions = {
      visibleColumns: defaultColumns.filter(col => col.default).map(col => col.displayName),
    };

    this.saveTableOptions();
  }

  public saveTableOptions() {
    localStorage.setItem('ui.options.contractorWorkers', JSON.stringify(this.tableOptions));
  }

  public setupColumnOptions() {
    const savedOptions = localStorage.getItem('ui.options.contractorWorkers');

    if (savedOptions) {
      this.tableOptions = JSON.parse(savedOptions);
      this.togglableColumns
        .filter(col => col.default && !this.tableOptions.visibleColumns.includes(col.displayName))
        .forEach(col => this.tableOptions.visibleColumns.push(col.displayName));
      this.saveTableOptions();
    } else {
      this.resetColumnOptions();
    }
  }
}
